import {
  Autocomplete,
  Button,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Grid from "@mui/material/Grid2";
import { useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
} from "../../../../../features/userSlice";
import { useEffect, useMemo, useState } from "react";
import { ActionItemsEndPointLogic } from "../../../../../logic/AssetManagement/ActionItems/actionItemsEndPointLogic";
import store from "../../../../../store";
import { useDispatch } from "react-redux";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import ActionItemsTableCard from "../ActionItemsTable/ActionItemsTableCard";
import {
  setActionItemsList,
  setSelectedActionItem,
  setSelectedAssignee,
  setSelectedCategory,
  setSelectedDueDate,
  setSelectedPriority,
  setSelectedSearchSite,
  setSelectedStatus,
  setSelectedTarget,
} from "../../../../../features/actionItemsSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../../Page404/Page403";

const ActionItemsSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const response = useSelector(selectUserData);
  const [loading, setLoading] = useState(false);
  const selectedSharedUid = useSelector(
    (state: any) => state.actionItems?.selectedSearchSite
  );
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [isDataFetch, setIsDataFetch] = useState(false);
  const dispatch = useDispatch();

  //-- Get User Permissions for Action Items
  const hasViewActionItemsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "view_action_items"
  );

  //--set SearchTerm from URL
  useEffect(() => {
    searchParams.get("selectedSite")
      ? setSearchTerm(searchParams.get("selectedSite") as string)
      : setSearchTerm("");
  }, [searchParams]);

  //-- Reset actionItemsList When selectedSharedUid empty
  useEffect(() => {
    if (selectedSharedUid === "") dispatch(setActionItemsList([]));
    // eslint-disable-next-line
  }, [!selectedSharedUid]);

  //-- this function is for set and delete the site id of the Current URL Path
  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get search query parameters from current URL Path

    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the query parameter value
    } else {
      updatedParams.delete("selectedSite"); // Remove query parameter if null
    }

    setSearchParams(updatedParams, { replace: true }); // update the URL's query parameters without reloading the page
  };

  //--Function to clear filters and clear query parameters
  const clearFilters = () => {
    setSearchParams({}); // clear all URL's query parameters
    dispatch(setSelectedActionItem(null));
    dispatch(setSelectedCategory(null));
    dispatch(setSelectedStatus(null));
    dispatch(setSelectedDueDate(null));
    dispatch(setSelectedPriority(null));
    dispatch(setSelectedAssignee(null));
    dispatch(setSelectedTarget(null));
    dispatch(setActionItemsList([]));
  };

  const fetchActionItems = async (selectedSharedUid: any) => {
    if (selectedSharedUid) {
      // dispatch(setSelectedSearchSite(selectedSharedUid));
      try {
        setLoading(true);
        await ActionItemsEndPointLogic.getAllActionItems(
          selectedSharedUid,
          oauthTokenHeader,
          dispatch
        );
        setIsDataFetch(true); // this for hit the API call
      } catch (error: any) {
        store.dispatch({ type: "error", payload: { message: error } });
      } finally {
        setLoading(false);
      }
    }
  };

  //-- Get website list those have permisssions
  const siteOptions = useMemo(() => {
    return (
      response?.data?.website_permissions?.map((siteObj: any) => {
        const shared_uid = Object.keys(siteObj)[0]; // shared_uid of the site
        const site_name = siteObj[shared_uid].ui_site_name; // Site name
        return { shared_uid, site_name }; // Return as an object with shared_uid and site_name
      }) || []
    );
  }, [response]);

  //-- clicking the select site button
  const handleSelectSite = async () => {
    if (!selectedSharedUid) return; //if no selectedSharedUid  then return false
    dispatch(setSelectedSearchSite(selectedSharedUid));
    updateURLParams(selectedSharedUid);
    fetchActionItems(selectedSharedUid); // API call
  };

  //-- Set / Delete the selected site(URL's query parameter) According to URL selectedSite id (shared UID)
  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite"); // Get query parameter from URL
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("actionItemSharedUid"); // Remove themeSharedUid from URL

    setSearchParams(updatedParams, { replace: true }); // Update URL query parameter without reloading

    if (
      siteFromURL &&
      siteOptions?.find((site: any) => site.shared_uid === siteFromURL)
    ) {
      dispatch(setSelectedSearchSite(siteFromURL)); // Set the selected site
    }
    // eslint-disable-next-line
  }, [siteOptions]);

  //-- open the link in a new tab when shared UID is selected or set in the URL's query parameter
  useEffect(() => {
    if (searchTerm) {
      fetchActionItems(searchTerm);
    }
    // eslint-disable-next-line
  }, [searchTerm]);

  // --- This for if user put invalid shared UID in the URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const selectedSharedUid = searchParams.get("selectedSite");

    // Check if the selectedSharedUid exists in siteOptions
    const isValid = siteOptions.some(
      (site: any) => site.shared_uid === selectedSharedUid
    );

    if (selectedSharedUid && !isValid) {
      searchParams.delete("selectedSite"); // Remove invalid parameter
      navigate(`?${searchParams.toString()}`, { replace: true }); // Redirect to default page
      dispatch(setSelectedSearchSite(null)); // Clear Redux state
    }
  }, [location.search, siteOptions, dispatch, navigate]);

  return (
    <>
      {hasViewActionItemsPermission ? (
        <>
          <Paper sx={{ padding: "8px 16px", mb: 2 }}>
            <Typography fontSize={24} sx={{ paddingBottom: "15px" }}>
              Action Items
            </Typography>
            <Grid container mt={0} columnSpacing={3} mb={1}>
              <Grid size={10}>
                <Autocomplete
                  fullWidth
                  disablePortal
                  options={siteOptions}
                  getOptionLabel={(option: any) => option?.site_name || ""}
                  value={
                    siteOptions?.find(
                      (option: any) => option?.shared_uid === selectedSharedUid
                    ) || null
                  }
                  onChange={(_event, newValue: any) => {
                    if (newValue?.shared_uid !== selectedSharedUid) {
                      clearFilters(); // Only clear when actual change happens
                      setIsDataFetch(false);
                      if (newValue) {
                        dispatch(setSelectedSearchSite(newValue?.shared_uid));
                      } else {
                        updateURLParams(null);
                        dispatch(setSelectedSearchSite(""));
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Asset"
                      size="small"
                      color="secondary"
                      placeholder="Search site name or choose from the list"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={2}>
                <Button
                  data-tracker="108"
                  variant="contained"
                  fullWidth
                  disabled={!selectedSharedUid}
                  onClick={handleSelectSite}
                  size="large"
                  color="secondary"
                  sx={{ py: "7px", px: "10px", whiteSpace: "nowrap" }}
                >
                  Select Site
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {loading && <SimpleBackdrop />}
          {!loading && selectedSharedUid && isDataFetch && (
            <ActionItemsTableCard />
          )}
        </>
      ) : (
        <Page404 />
      )}
    </>
  );
};

export default ActionItemsSearch;
